import HuskyTooltip from "@husky-hrc/Components/FormFields/HuskyTooltip";
import InputSubText from "@husky-hrc/Components/FormFields/InputSubText";
import RevertButton from "@husky-hrc/Components/FormFields/Revert";
import { InputModel, ValueModel } from "@husky-hrc/Framework/Types/ApiModel";
import { inputStyles, labelStyles } from "@husky-hrc/Utilities/Mixins/form";
import { moveFocus } from "@husky-hrc/Utilities/Utilities";
import { ProductConfigViewContext } from "@husky-hrc/Widgets/ProductConfigView";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ListItem from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { MouseEvent } from "react";
import styled, { css } from "styled-components";

export interface SelectFieldStyles {
    selectContainerStyles?: InjectableCss;
    labelContainerStyles?: InjectableCss;
    infoTextStyles?: TypographyPresentationProps;
    selectIconStyles?: InjectableCss;
    selectDropdownStyles?: InjectableCss;
    selectInnerText?: TypographyPresentationProps;
}

export const selectFieldStyles: SelectFieldStyles = {
    selectContainerStyles: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    labelContainerStyles: {
        css: css`
            margin-bottom: 20px;
            display: flex;
            font-size: 20px;
            align-items: center;
            ${labelStyles()}
        `,
    },
    infoTextStyles: {
        css: css`
            font-size: 16px;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    selectIconStyles: {
        css: css`
            position: absolute;
            top: 12px;
            right: 6px;
            color: ${getColor("danger.main")};
            height: 20px;
            width: 14px;
            pointer-events: none;
        `,
    },
    selectInnerText: {
        css: css`
            display: inline-block;
            width: 100%;
            white-space: nowrap;
            padding-right: 15px;
            text-overflow: ellipsis;
            vertical-align: middle;
            line-height: 48px;
            overflow: hidden;
        `,
    },
};

const SelectBox = styled.span<{ hasError: boolean }>`
    ${inputStyles()}
    display: flex;
    align-items: center;
    padding: 0 10px;
    margin-top: 10px;
    font-size: 16px;
    position: relative;
`;

const SelectOptions = styled.ul<{ show: boolean; flip: boolean }>`
    visibility: ${props => (props.show ? "visible" : "hidden")};
    position: absolute;
    left: 0;
    top: ${props => (props.flip ? "unset" : "100%")};
    bottom: ${props => (props.flip ? "100%" : "unset")};
    width: 100%;
    background: ${getColor("common.background")};
    list-style: none;
    box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.25);
    border: 1px solid ${getColor("common.backgroundContrast")};
    padding: 0;
    margin: 2px 0 2px 0;
    max-height: 300px;
    overflow: auto;
    user-select: none;
    z-index: 99;
    > li {
        min-height: 40px;
        font-size: 16px;
        padding: 0 20px;
        display: flex;
        align-items: center;
        &:first-child {
            padding-top: 7px;
        }
        &.green:hover {
            background: rgba(222, 44, 15, 0.2);
        }
        &.orange {
            font-style: italic;
            background: ${getColor("common.disabled")};
            &.hidden {
                display: none;
            }
        }
    }
`;

interface OwnProps {
    cssHideOrange: boolean;
    input: InputModel;
    onChange: (parameter: string, value: string) => void;
    onUncommit: (parameter: string) => void;
}

type Props = OwnProps;

const styles = selectFieldStyles;

const SelectField = ({ cssHideOrange, input, onChange, onUncommit }: Props) => {
    const [value, setValue] = React.useState(input.value);
    const [isOpen, setIsOpen] = React.useState(false);
    const productConfigViewContext = React.useContext(ProductConfigViewContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [flipDropdown, setFlipDropdown] = React.useState(true);

    React.useEffect(() => {
        window.addEventListener("scroll", getFlipDropdown);
        getFlipDropdown();
    }, []);

    React.useEffect(() => {
        setValue(input.value);
    }, [input]);

    React.useEffect(() => {
        productConfigViewContext.validators[input.name] = validate;
        return () => {
            delete productConfigViewContext.validators[input.name];
        };
    }, [value]);

    React.useEffect(() => {
        const selector = `li[data-name='${input.name}']`;
        const selectOptions = [...document.querySelectorAll(selector)] as HTMLElement[];
        selectOptions[0].focus();
    }, [isOpen]);

    const validate = (inputValue: string | null = value) => {
        if (input.required && !inputValue) {
            setErrorMessage(translate("This field is required"));
            return false;
        }
        setErrorMessage("");
        return true;
    };

    const handleSelectChange = (event: MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLLIElement>) => {
        const element = event.target as HTMLInputElement;
        const parameter = element.getAttribute("data-name");
        const valueName = element.getAttribute("data-value");
        const valueDescription = element.getAttribute("data-value-description");
        setValue(valueDescription!);
        const defaultValue = input.defaultValue.name;
        const isValid = validate(valueDescription);
        if (!isValid) {
            return;
        }

        if ((valueName !== defaultValue || !input.committed) && parameter && valueName) {
            onChange(parameter, valueName);
        }
        setIsOpen(false);
    };

    const handleBlur = (event: React.FocusEvent) => {
        const selector = `[data-id='${input.name}_select']`;
        const element = document.querySelector(selector);
        const focusTargetIsChild = element?.contains(event.relatedTarget as Node);
        if (!focusTargetIsChild) {
            setIsOpen(false);
            validate();
        }
    };

    const handleSelectKeyDown = (event: React.KeyboardEvent) => {
        const key = event.nativeEvent.code.toLowerCase();
        const selector = `li[data-name='${input.name}']`;
        const selectOptions = [...document.querySelectorAll(selector)] as HTMLElement[];
        if (key === "enter" || key === "space") {
            event.preventDefault();
            setIsOpen(!isOpen);
        } else if (key === "arrowdown" || key === "arrowup") {
            event.preventDefault();
            if (key === "arrowdown" && !isOpen) {
                setIsOpen(true);
            } else {
                moveFocus(key, selectOptions, false);
            }
        } else if ((key === "tab" || key === "escape") && isOpen) {
            setIsOpen(!isOpen);
        } else {
            moveFocus(event.key, selectOptions, false);
        }
    };

    const handleItemKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        const key = event.nativeEvent.code.toLowerCase();
        if (key === "enter" || key === "space") {
            event.preventDefault();
            handleSelectChange(event);
        }
    };

    const getFlipDropdown = () => {
        const footer = document.querySelector("[data-id='tactonConfigFooter']");
        const boxSelector = `[data-id=${input.name}_select]`;
        const selectBox = document.querySelector(boxSelector);
        const optionSelector = `[data-id=${input.name}_options]`;
        const options = document.querySelector(optionSelector);
        if (footer && selectBox && options) {
            const { top } = footer.getBoundingClientRect();
            const { bottom } = selectBox.getBoundingClientRect();
            const { height } = options.getBoundingClientRect();
            setFlipDropdown(bottom + height + 2 > top);
            return;
        }
        setFlipDropdown(false);
    };

    const cssHideOrangeState = (stateClass: string | undefined) => {
        if (stateClass) {
            return cssHideOrange === true ? `hidden ${stateClass}` : stateClass;
        }
    };

    return (
        <StyledWrapper {...styles.selectContainerStyles}>
            <StyledWrapper {...styles.labelContainerStyles}>
                <span id={`${input.name}_label`} data-required={input.required}>
                    {input.description}
                </span>
                {input.tooltip && <HuskyTooltip input={input} />}
                {input.committed && <RevertButton input={input} onUncommit={onUncommit} />}
            </StyledWrapper>
            <Typography id={`${input.name}_tcInfoText`} as="p" {...styles.infoTextStyles}>
                {input.tcInfoText}
            </Typography>
            <SelectBox
                role="combobox"
                aria-required={input.required}
                data-id={`${input.name}_select`}
                data-committed={input.committed}
                tabIndex={0}
                hasError={!!errorMessage}
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleSelectKeyDown}
                onBlur={handleBlur}
                aria-haspopup="listbox"
                aria-controls={`${input.name}_listbox`}
                aria-expanded={isOpen}
                aria-labelledby={`${input.name}_label`}
                aria-describedby={`${input.name}_tcInfoText ${input.name}_subText`}
                aria-invalid={!!errorMessage}
            >
                <Typography {...styles.selectInnerText}>{value || input.placeholder}</Typography>
                <Icon {...styles.selectIconStyles} src={ChevronDown} />
                <SelectOptions
                    id={`${input.name}_listbox`}
                    role="listbox"
                    data-id={`${input.name}_options`}
                    show={isOpen}
                    flip={flipDropdown}
                    onMouseDown={e => e.preventDefault()}
                    aria-labelledby={`${input.name}_label`}
                >
                    {input.values.map((val: ValueModel, index: number) => (
                        // eslint-disable-next-line react/jsx-key
                        <ListItem
                            key={index}
                            id={val.description}
                            role="option"
                            tabIndex={0}
                            className={cssHideOrangeState(val.state)}
                            data-name={input.name}
                            data-value={val.name}
                            data-value-description={val.description}
                            onClick={handleSelectChange}
                            onKeyDown={handleItemKeyDown}
                            aria-selected={value === val.description}
                        >
                            {val.description}
                        </ListItem>
                    ))}
                </SelectOptions>
            </SelectBox>
            <InputSubText id={`${input.name}_subText`} input={input} errorMessage={errorMessage} />
        </StyledWrapper>
    );
};

export default SelectField;
